// The alerts panel — the AM's queue of what needs work.
//
// ROW LAYOUT (round 2): one row per alert, laid out as a COLUMN RHYTHM rather
// than a sentence — severity dot · account · type badge · platform badge ·
// reason · FIGURE · actions — so the eye can run down a single column ("show me
// all the figures") instead of re-reading prose on every line.
//
// ⚠️ THE FIGURE IS THE HERO, NOT A FOOTNOTE. It is the evidence the AM acts on
// ("NZ$97.00 across the prior 7 days, then NZ$0.00"), so it renders at full
// contrast in tabular numerals — the REASON is the muted supporting text, which
// is the opposite of how this started out.
//
// COLLAPSIBLE (persisted in the page's view prefs): collapsed shows the header
// + severity counts so nothing is hidden silently; the verdict strip above
// stays visible either way. The type chips + platform toggle narrow this
// panel's list ONLY — the book table's status chips keep reflecting ALL alert
// types (the table is ground truth; see src/lib/advertisingHome/alertsView.ts).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, ChevronRight, MessageSquare, Undo2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { AlertType, HomeAlert } from '../../lib/advertisingHome/alerts';
import { UNASSIGNED_AM_LABEL } from '../../lib/advertisingHome/bookFilter';
import type { AlertNote } from '../../types/advertisingHome';
import {
  ALERT_TYPE_OPTIONS,
  capAlerts,
  filterAlertsByPlatform,
  filterAlertsByType,
  severityCounts,
  type AlertPlatformFilter,
} from '../../lib/advertisingHome/alertsView';
import {
  ACK_WINDOW_HOURS,
  formatAckedBy,
  type AcknowledgedAlert,
} from '../../lib/advertisingHome/acknowledgements';
import { AlertTypeBadge, ChannelChip, SeverityDot } from './AlertBadges';
import { channelReportingHref } from './deepLinks';
import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';
import type { AccountLinkKey } from '../../lib/accountLinks';

// The shared column rhythm. Below `md` the row degrades to a wrapping flex line
// (the page is a desktop tool, like the reporting dashboards it sits beside).
//
// ⚠️ TWO TEMPLATES, ONE PER SURFACE. The specialist boards carry an extra
// ACCOUNT-MANAGER column (§11a); the AM board does not, because its whole book
// is one manager's and the name on every row would be noise. Derived from the
// same boolean the cell is, so the header count and the template cannot drift.
const ROW_GRID_BASE = 'flex flex-wrap items-center gap-x-3 gap-y-1 md:grid';
const ROW_COLS_AM =
  'md:grid-cols-[auto_minmax(8rem,1.1fr)_auto_auto_minmax(6rem,1.3fr)_minmax(8rem,1.2fr)_auto]';
const ROW_COLS_SPECIALIST =
  'md:grid-cols-[auto_minmax(8rem,1.1fr)_minmax(5rem,0.7fr)_auto_auto_minmax(6rem,1.3fr)_minmax(8rem,1.2fr)_auto]';

const rowGrid = (showManager: boolean): string =>
  cn(ROW_GRID_BASE, showManager ? ROW_COLS_SPECIALIST : ROW_COLS_AM);

/**
 * ⚠️ AN ACCOUNT WITH NO MANAGER RENDERS THE WORD, NEVER AN EMPTY GAP — the same
 * posture as the filter's `UNASSIGNED_AM_ID` sentinel (§9a). An account nobody
 * owns is exactly the one a specialist most needs to see named, and a blank
 * cell reads as a rendering fault rather than as a fact.
 *
 * ⚠️ THE LABEL IS THE FILTER'S OWN, IMPORTED — not a second literal. The AM
 * filter already offers "Unassigned" as a selectable value, and a row labelled
 * one way while the control that selects it says another is exactly the drift
 * one shared constant exists to prevent.
 */
function ManagerCell({ name }: { name: string | null }): React.JSX.Element {
  const label = name ?? UNASSIGNED_AM_LABEL;
  return (
    <span
      className={cn('min-w-0 truncate text-xs', name ? 'text-muted-foreground' : 'text-amber-700')}
      title={`Account manager — ${label}`}
    >
      {label}
    </span>
  );
}

/**
 * §11b — "someone has written something here".
 *
 * ⚠️⚠️ IT MUST NOT READ AS "HANDLED". A note and an ack are INDEPENDENT (§3d — a
 * note without an ack is an explicitly supported state, and is the point of
 * "waiting on the client, don't clear"). So this is deliberately NOT a tick, NOT
 * green, and NOT the same shape as the Ack control beside it: it is the notes
 * icon in the notes' own colour, with the count, saying only that a thread
 * exists. The title says so in words.
 */
function NoteBadge({ count }: { count: number }): React.JSX.Element | null {
  if (count <= 0) return null;
  return (
    <span
      className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[11px] font-medium text-sky-800"
      title={`${count} handoff ${count === 1 ? 'note' : 'notes'} — someone has written something here. This does NOT mean it has been handled.`}
    >
      <MessageSquare className="h-3 w-3" aria-hidden />
      {count}
    </span>
  );
}

// The account column: the name (truncating) plus, when opted in, its external
// link icons, which never shrink — the name gives way, not the links.
function AccountNameCell({
  name,
  accountId,
  showLinkIcons,
  linkKeys,
  strong = false,
}: {
  name: string;
  accountId: string;
  showLinkIcons: boolean;
  linkKeys?: AccountLinkKey[];
  strong?: boolean;
}): React.JSX.Element {
  const label = (
    <span className={cn('min-w-0 truncate text-sm', strong && 'font-medium')} title={name}>
      {name}
    </span>
  );
  if (!showLinkIcons) return label;
  return (
    <span className="flex min-w-0 items-center gap-1">
      {label}
      <AccountLinkIcons accountId={accountId} className="shrink-0" only={linkKeys} />
    </span>
  );
}

function AlertRow({
  a,
  onAcknowledge,
  busy,
  extra,
  managerName,
  showManager,
  noteCount,
  showLinkIcons,
  linkKeys,
}: {
  a: HomeAlert;
  onAcknowledge: (alert: HomeAlert) => void;
  busy: boolean;
  /**
   * ⚠️ OPTIONAL, and ABSENT ON THE AM BOARD. The specialist pages render the
   * handoff notes here; Advertising Home passes nothing and is unchanged, which
   * is the point — one AlertsPanel, no fork, and no page-content change to a
   * board that was explicitly out of scope.
   */
  extra?: React.ReactNode;
  managerName: string | null;
  showManager: boolean;
  noteCount: number;
  showLinkIcons: boolean;
  linkKeys?: AccountLinkKey[];
}): React.JSX.Element {
  return (
    <li className="px-3 py-2">
      <div className={rowGrid(showManager)}>
      <SeverityDot severity={a.severity} />
      <AccountNameCell name={a.accountName} accountId={a.accountId} showLinkIcons={showLinkIcons} linkKeys={linkKeys} strong />
      {/* §11a — WHO TO HAND IT BACK TO. Specialist boards only: an AM's own
          board is one manager's book, where the name on every row is noise. */}
      {showManager && <ManagerCell name={managerName} />}
      <AlertTypeBadge type={a.type} />
      <ChannelChip channel={a.channel} />
      <span className="min-w-0 truncate text-sm text-muted-foreground" title={a.reason}>
        {a.reason}
      </span>
      {/* The hero. Full contrast + tabular so the column scans vertically. */}
      <span className="min-w-0 truncate text-sm font-medium tabular-nums text-foreground" title={a.figure}>
        {a.figure}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {/* §11b — a thread exists. NOT a "handled" marker; see NoteBadge. */}
        <NoteBadge count={noteCount} />
        <button
          type="button"
          onClick={() => onAcknowledge(a)}
          disabled={busy}
          title={`Acknowledge — hides this for ${ACK_WINDOW_HOURS}h unless it worsens`}
          className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
        >
          <Check className="h-3 w-3" aria-hidden />
          Ack
        </button>
        <Link
          to={channelReportingHref(a.channel, a.accountId)}
          className="inline-flex items-center gap-1 text-xs font-medium text-kyck-700 hover:underline"
        >
          View
          <ArrowRight className="h-3 w-3" aria-hidden />
        </Link>
        </span>
      </div>
      {extra}
    </li>
  );
}

/** A suppressed alert — same rhythm, dimmed, with who/when + un-ack. */
function AcknowledgedRow({
  entry,
  onUnacknowledge,
  busy,
  managerName,
  showManager,
  noteCount,
  showLinkIcons,
  linkKeys,
}: {
  entry: AcknowledgedAlert;
  onUnacknowledge: (entry: AcknowledgedAlert) => void;
  busy: boolean;
  managerName: string | null;
  showManager: boolean;
  noteCount: number;
  showLinkIcons: boolean;
  linkKeys?: AccountLinkKey[];
}): React.JSX.Element {
  const a = entry.alert;
  return (
    <li className={cn(rowGrid(showManager), 'bg-muted/30 px-3 py-2')}>
      <SeverityDot severity={a.severity} className="opacity-40" />
      <AccountNameCell name={a.accountName} accountId={a.accountId} showLinkIcons={showLinkIcons} linkKeys={linkKeys} />
      {/* ⚠️ The SAME column on both row types, so a cleared alert still names
          who owns the account — that is exactly when a specialist is deciding
          whether to hand it back rather than clear it again. */}
      {showManager && <ManagerCell name={managerName} />}
      <AlertTypeBadge type={a.type} />
      <ChannelChip channel={a.channel} />
      {/* Who cleared it, and when — the attribution that makes a team-wide ack
          accountable rather than anonymous. */}
      <span className="min-w-0 truncate text-xs text-muted-foreground">
        {formatAckedBy(entry.acknowledgement, Date.now())}
      </span>
      <span className="min-w-0 truncate text-sm tabular-nums text-muted-foreground" title={a.figure}>
        {a.figure}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <NoteBadge count={noteCount} />
        <button
          type="button"
          onClick={() => onUnacknowledge(entry)}
          disabled={busy}
          className="inline-flex shrink-0 items-center gap-1 rounded border border-border px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
        >
          <Undo2 className="h-3 w-3" aria-hidden />
          Un-ack
        </button>
      </span>
    </li>
  );
}

const PLATFORM_OPTIONS: Array<{ value: AlertPlatformFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'meta', label: 'Meta' },
  { value: 'google', label: 'Google' },
];

export function AlertsPanel({
  alerts,
  acknowledged,
  collapsed,
  onToggleCollapsed,
  typeFilter,
  onTypeFilterChange,
  platformFilter,
  onPlatformFilterChange,
  onAcknowledge,
  onUnacknowledge,
  busy = false,
  renderAlertExtra,
  notesFor,
  managerFor,
  showLinkIcons = false,
  linkKeys,
}: {
  /** Already health-filtered by the verdict strip AND ack-filtered. */
  alerts: HomeAlert[];
  /** Suppressed by an ack — counted, revealable, never silently dropped. */
  acknowledged: AcknowledgedAlert[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
  typeFilter: ReadonlySet<AlertType>;
  onTypeFilterChange: (next: Set<AlertType>) => void;
  platformFilter: AlertPlatformFilter;
  onPlatformFilterChange: (next: AlertPlatformFilter) => void;
  onAcknowledge: (alert: HomeAlert) => void;
  onUnacknowledge: (entry: AcknowledgedAlert) => void;
  busy?: boolean;
  /**
   * ⚠️ OPTIONAL — the specialist boards pass the handoff-notes thread; the AM
   * board passes nothing and renders exactly as before. Additive on purpose:
   * changing Advertising Home's page content is out of scope, and forking the
   * panel would give the alert queue two implementations to keep in step.
   *
   * ⚠️⚠️ IT IS HANDED THE NOTES, rather than fetching them itself — see
   * `notesFor`. That is what makes the §11b badge and this thread the SAME
   * list by construction.
   */
  renderAlertExtra?: (alert: HomeAlert, notes: readonly AlertNote[]) => React.ReactNode;
  /**
   * §11b — this alert's notes.
   *
   * ⚠️⚠️ ONE ACCESSOR FEEDS BOTH THE BADGE AND THE THREAD, called ONCE per row.
   * The brief's requirement is that the marker be driven off the same note list
   * the panel renders, "not a separate count query — a badge that disagrees
   * with the notes underneath it is worse than no badge". A `noteCount` prop
   * beside a separate `renderAlertExtra` would be two sources that agree only
   * by care; this is one source that cannot disagree with itself.
   */
  notesFor?: (alert: HomeAlert) => readonly AlertNote[];
  /**
   * §11a — uid-resolved account-manager name, or null for an unowned account
   * (rendered "Unassigned", never a gap). ⚠️ ABSENT ON THE AM BOARD, which is
   * what keeps the extra column off it.
   */
  managerFor?: (alert: HomeAlert) => string | null;
  /**
   * Round 2 item 3 — the account's external link icons beside its name.
   * ⚠️ OPT-IN, and only Advertising Home passes it for now: this panel is shared
   * with the Meta and Google boards, and the link icons stop at Advertising Home
   * until Scott has looked at the density on a real screen.
   */
  showLinkIcons?: boolean;
  /** Restrict the icon strip to these links (e.g. Meta Home: the Meta ad account only). */
  linkKeys?: AccountLinkKey[];
}): React.JSX.Element {
  // ⚠️ The COLUMN and the CELL are gated on the same value, so a board cannot
  // end up with a template that has a slot nothing fills (or the reverse).
  const showManager = managerFor !== undefined;
  const notesOf = (alert: HomeAlert): readonly AlertNote[] => notesFor?.(alert) ?? [];
  // Session-only view toggles — page-length guards, not preferences.
  const [showAll, setShowAll] = useState(false);
  const [showAcked, setShowAcked] = useState(false);

  const counts = severityCounts(alerts);
  const listed = filterAlertsByPlatform(filterAlertsByType(alerts, typeFilter), platformFilter);
  const { visible, hidden } = capAlerts(listed, showAll);
  // The same two filters narrow the acknowledged list, so the panel stays one
  // coherent view rather than two lists answering different questions.
  const ackedEntries = acknowledged.filter((e) => {
    const keep = filterAlertsByPlatform(
      filterAlertsByType([e.alert], typeFilter),
      platformFilter,
    );
    return keep.length > 0;
  });

  const toggleType = (t: AlertType): void => {
    const next = new Set(typeFilter);
    if (next.has(t)) next.delete(t);
    else next.add(t);
    onTypeFilterChange(next);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent/40"
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        )}
        <span className="text-xs font-semibold">Alerts</span>
        {/* The counts render in BOTH states, so collapsing hides nothing silently. */}
        <span className="text-xs text-muted-foreground">
          {alerts.length === 0 ? (
            'none — the book is quiet'
          ) : (
            <>
              <span className="font-medium text-red-700">{counts.red} critical</span>
              {' · '}
              <span className="font-medium text-amber-700">{counts.amber} attention</span>
            </>
          )}
          {acknowledged.length > 0 && (
            <span className="ml-1">· {acknowledged.length} acknowledged</span>
          )}
        </span>
      </button>

      {!collapsed && (alerts.length > 0 || acknowledged.length > 0) && (
        <>
          {/* Type isolation + platform toggle — both filter THIS list only. */}
          <div className="flex flex-wrap items-center gap-1.5 border-t border-border px-3 py-2">
            {ALERT_TYPE_OPTIONS.map((o) => {
              const on = typeFilter.has(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggleType(o.value)}
                  aria-pressed={on}
                  className={cn(
                    'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                    on
                      ? 'border-kyck-300 bg-kyck-50 font-medium text-kyck-800'
                      : 'border-border bg-card text-muted-foreground hover:bg-accent',
                  )}
                >
                  {o.label}
                </button>
              );
            })}
            <span className="mx-1 h-4 w-px bg-border" aria-hidden />
            {/* Platform: a single-choice segmented control (All is not "none"). */}
            <span className="inline-flex overflow-hidden rounded-md border border-border">
              {PLATFORM_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => onPlatformFilterChange(o.value)}
                  aria-pressed={platformFilter === o.value}
                  className={cn(
                    'px-2.5 py-0.5 text-xs transition-colors',
                    platformFilter === o.value
                      ? 'bg-accent font-medium text-foreground'
                      : 'text-muted-foreground hover:bg-accent/50',
                  )}
                >
                  {o.label}
                </button>
              ))}
            </span>
          </div>

          {listed.length === 0 ? (
            <p className="border-t border-border px-3 py-3 text-sm text-muted-foreground">
              {alerts.length === 0
                ? 'Nothing outstanding — everything here is acknowledged.'
                : 'No alerts of the selected types.'}
            </p>
          ) : (
            <>
              <ul className="divide-y divide-border border-t border-border">
                {visible.map((a) => {
                  // ⚠️ READ ONCE — the badge below and the thread inside
                  // `renderAlertExtra` are literally the same array.
                  const notes = notesOf(a);
                  return (
                    <AlertRow
                      key={`${a.accountId}-${a.channel}-${a.type}`}
                      a={a}
                      onAcknowledge={onAcknowledge}
                      busy={busy}
                      extra={renderAlertExtra?.(a, notes)}
                      managerName={managerFor?.(a) ?? null}
                      showManager={showManager}
                      showLinkIcons={showLinkIcons}
                      linkKeys={linkKeys}
                      noteCount={notes.length}
                    />
                  );
                })}
              </ul>
              {hidden > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="w-full border-t border-border px-3 py-2 text-center text-xs font-medium text-kyck-700 hover:bg-accent/40"
                >
                  Show all {listed.length}
                </button>
              )}
            </>
          )}

          {/* Acknowledged — counted above and revealable here, so an ack hides
              work from the QUEUE without hiding it from the PAGE. */}
          {ackedEntries.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowAcked((v) => !v)}
                aria-expanded={showAcked}
                className="w-full border-t border-border px-3 py-2 text-left text-xs text-muted-foreground hover:bg-accent/40"
              >
                {showAcked ? 'Hide' : 'Show'} {ackedEntries.length} acknowledged
              </button>
              {showAcked && (
                <ul className="divide-y divide-border border-t border-border">
                  {ackedEntries.map((e) => (
                    <AcknowledgedRow
                      key={e.acknowledgement.id}
                      entry={e}
                      onUnacknowledge={onUnacknowledge}
                      busy={busy}
                      managerName={managerFor?.(e.alert) ?? null}
                      showManager={showManager}
                      showLinkIcons={showLinkIcons}
                      linkKeys={linkKeys}
                      noteCount={notesOf(e.alert).length}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
